/**
 * @component PhysioVideoSelection
 * @description Video selection and management interface for physiotherapy practitioners.
 * Allows therapists to:
 * - Browse Desk Dynamics and physio-specific videos
 * - Filter videos by exercise type preferences
 * - Select multiple videos to send to clients
 * - View client history and pre-populate previous video selections
 * - Manage account settings, language preferences, and notifications
 * - Watch tutorial videos
 *
 * @flow
 * 1. Load all videos (DD + physio) and client history via React Query
 * 2. Display videos in tabbed interface (DD vs Physio)
 * 3. Allow filtering by preference type and search term
 * 4. Enable video selection (multi-select with visual feedback)
 * 5. Show selected videos dialog with client details (name, email, duration)
 * 6. Send selected videos to client via backend API
 * 7. Show success/error notification
 * 8. Clear selection and reset dialog
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormHelperText,
  InputAdornment,
  AppBar,
  Toolbar,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import VideoComponent from "../videoplayer/VideoComponent";
import BasketIcon from "@mui/icons-material/ShoppingBasket";
import HomeIcon from "@mui/icons-material/Home";
import HelpIcon from "@mui/icons-material/Help";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

// Context and Services
import { useVideosQuery } from "../stores/tanstack/queries/useVideosQuery";
import { useTherapistHistoryQuery } from "../stores/tanstack/queries/useTherapistHistoryQuery";
import { useAuthStore } from "../stores/zustand";
import UserAccountPopover from "../components/layout/UserAccountPopover";
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";
import { authApi, settingsApi } from "../services";
import { useQueryClient } from "@tanstack/react-query";

/**
 * PhysioVideoSelection Component
 * Main page for physiotherapy practitioners to manage video selection and client assignments
 */
const PhysioVideoSelection = () => {
  const { t, i18n } = useTranslation(); // Translation hook for multi-language support
  const navigate = useNavigate(); // React Router navigation

  // Fetch all videos (Desk Dynamics + physiotherapy-specific)
  const {
    data: videosData,
    isLoading: videosLoading,
    error: videosError,
  } = useVideosQuery();

  // Fetch therapist's client history for quick re-selection
  const { clients: clientsData } = useTherapistHistoryQuery();

  // Memoize video arrays to prevent unnecessary re-renders
  const { allVideos, physioVideos } = useMemo(
    () => ({
      allVideos: videosData?.allVideos || [],
      physioVideos: videosData?.physioVideos || [],
    }),
    [videosData?.allVideos, videosData?.physioVideos],
  );

  const {
    currentProduct,
    clearAuth,
    user,
    preferredLanguage,
    setPreferredLanguage,
  } = useAuthStore(); // Current user context
  const isPhysioTherapist = currentProduct === "physio_therapist"; // Check if logged-in user is therapist
  const userEmail = user.email; // Current therapist's email
  const clients = clientsData || []; // List of therapist's past clients
  const queryClient = useQueryClient(); // TanStack Query client for cache management

  const [activeTab, setActiveTab] = useState("dd"); // Currently displayed tab: 'dd' (Desk Dynamics) or 'physio'

  const [selectedPreference, setSelectedPreference] = useState("all"); // Selected exercise preference filter
  const [filteredVideos, setFilteredVideos] = useState([]); // Videos matching filters
  const [searchTerm, setSearchTerm] = useState(""); // Search query text
  const [selectOpen, setSelectOpen] = useState(false); // Preference dropdown open state
  const [wasEverSelected, setWasEverSelected] = useState(false); // Validation flag for preference selector
  const [historySearchTerm, setHistorySearchTerm] = useState("");

  const [selectedVideos, setSelectedVideos] = useState(new Set()); // Set of selected video IDs
  const [dialogOpen, setDialogOpen] = useState(false); // Send-to-client dialog open state
  const [email, setEmail] = useState(""); // Client email input
  const [firstName, setFirstName] = useState(""); // Client first name input
  const [lastName, setLastName] = useState(""); // Client last name input
  const [number, setNumber] = useState(1); // Duration number (1-50)
  const [duration, setDuration] = useState("days"); // Duration unit (days, weeks, months)

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false); // Client history dialog state

  const [videoToPlay, setVideoToPlay] = useState(null); // Currently playing video object
  const [isPlaying, setIsPlaying] = useState(false); // Video player modal visibility

  const [anchorEl, setAnchorEl] = useState(null); // Account popover anchor element
  const [videoDialogOpen, setVideoDialogOpen] = useState(false); // Tutorial video dialog state

  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar notification visibility
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message content
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Snackbar message type (info, success, warning, error)

  const [isLoggingOut, setIsLoggingOut] = useState(false); // Prevents redirect during logout

  // Ref for smooth scrolling to video player
  const videoPlayerRef = useRef(null);

  const isMobileView = useMediaQuery("(max-width:700px)");

  /**
   * Get current browser notification permission status
   * @returns {string} Notification.permission ('granted', 'denied', 'default')
   */
  const getNotificationPermission = () => {
    if (typeof window !== "undefined" && "Notification" in window) {
      return Notification.permission;
    }
    return "default";
  };

  /**
   * Check if app is running in Electron (desktop) environment
   * @returns {boolean} True if Electron APIs are available
   */
  const isElectron = !!(window.electronAPI || window.electronPushReceiver);

  /**
   * Auth guard: Redirect non-therapists to home page
   * Checks if logged-in user has physiotherapy_therapist product
   * Skips check during logout to prevent navigation interference
   */
  useEffect(() => {
    if (!isPhysioTherapist && !isLoggingOut) {
      navigate("/home", { replace: true });
    }
  }, [navigate, isPhysioTherapist, isLoggingOut]);

  /**
   * Filter videos based on active tab, preference, and search term
   * Dynamically updates filteredVideos when:
   * - User switches between DD and Physio tabs
   * - User changes preference filter
   * - User types in search box
   */

  // Filter videos based on active tab, preference, and search
  useEffect(() => {
    // Get the correct video array based on active tab
    const sourceVideos =
      activeTab === "physio"
        ? Array.isArray(physioVideos)
          ? physioVideos
          : []
        : Array.isArray(allVideos)
          ? allVideos
          : [];

    const filtered = sourceVideos.filter((video) => {
      // Preference filter
      const preferenceMatch =
        selectedPreference === "all" ||
        video.preferences?.some((pref) => pref.id === selectedPreference);

      // Search filter
      const searchTermMatch =
        searchTerm === "" ||
        video.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.description?.toLowerCase().includes(searchTerm.toLowerCase());

      return preferenceMatch && searchTermMatch;
    });

    setFilteredVideos(filtered);
  }, [allVideos, physioVideos, activeTab, selectedPreference, searchTerm]);

  /**
   * Handle preference dropdown open
   */
  const handleOpenSelect = () => {
    setSelectOpen(true);
  };

  const handleCloseSelect = () => {
    setSelectOpen(false);
  };

  /**
   * Handle preference filter change
   * Sets validation flag and updates selected preference ID
   */
  const handlePreferenceChange = (event) => {
    setWasEverSelected(true);
    const preferenceId = event.target.value;
    setSelectedPreference(preferenceId);
  };

  // Smooth scroll to video player with custom animation
  const scrollToVideoPlayer = useCallback(() => {
    setTimeout(() => {
      if (!videoPlayerRef.current) return;

      const targetPosition =
        videoPlayerRef.current.getBoundingClientRect().top + window.scrollY;
      const startPosition = window.scrollY;
      const distance = targetPosition - startPosition;
      const duration = 800; // ms - adjust for slower/faster scroll
      let startTime = null;

      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }, 100);
  }, []);

  /**
   * Handle tab switch between DD and Physio videos
   * Resets filters when switching tabs
   */
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Reset filters when switching tabs
    setSelectedPreference("all");
    setSearchTerm("");
  };

  /**
   * Toggle video selection (add to/remove from basket)
   * Uses Set for efficient duplicate checking
   */
  const toggleVideoSelection = (videoId) => {
    setSelectedVideos((prevSelectedVideos) => {
      const newSelectedVideos = new Set(prevSelectedVideos);
      if (newSelectedVideos.has(videoId)) {
        newSelectedVideos.delete(videoId);
      } else {
        newSelectedVideos.add(videoId);
      }
      return newSelectedVideos;
    });
  };

  /**
   * Play video in modal player
   * @param {Object} video - Video object to play
   */
  const handlePlayVideo = (video) => {
    setVideoToPlay(video);
    setIsPlaying(true);

    // Smooth scroll to video player
    scrollToVideoPlayer();
  };

  /**
   * Close video player modal
   */
  const handleCloseVideo = () => {
    setIsPlaying(false);
  };

  /**
   * Open send-to-client dialog if videos are selected
   */
  const handleDialogOpen = () => {
    if (selectedVideos.size !== 0) {
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  /**
   * Remove video from basket (selected videos)
   * Closes dialog if last video is removed
   */
  const handleRemoveVideo = (videoId) => {
    setSelectedVideos((prevSelectedVideos) => {
      const newSelectedVideos = new Set(prevSelectedVideos);
      newSelectedVideos.delete(videoId);
      return newSelectedVideos;
    });
    if (selectedVideos.size === 1) {
      handleDialogClose();
    }
  };

  /**
   * Send selected videos to client via backend API
   * Handles various API response statuses (user not found, settings off, success, etc.)
   * Shows snackbar notification with result
   * Clears selection after sending
   */
  const handleSend = async () => {
    // Validate email before sending
    if (!email.trim()) {
      setSnackbarMessage(t("errorSendingVideos"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_FIREBASE_API_HOST}/api/physio`,
        {
          userFirstName: firstName,
          userLastName: lastName,
          userEmail: email,
          videoIds: Array.from(selectedVideos),
          numberOfDays: duration === "days" ? parseInt(number) : 0,
          numberOfWeeks: duration === "weeks" ? parseInt(number) : 0,
          numberOfMonths: duration === "months" ? parseInt(number) : 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true, // Send cookies
        },
      );

      const { status } = response.data;
      let message = "";
      switch (status) {
        case "NEW_USER":
          message = t("noUserFound");
          setSnackbarSeverity("success");
          break;
        case "USER_SETTINGS_OFF":
          message = t("userSettingsOffline");
          setSnackbarSeverity("warning");
          break;
        case "NO_VIDEOS_FOUND":
          message = t("noVideosFound");
          setSnackbarSeverity("error");
          break;
        case "SUCCESS":
          message = t("sentSuccessfully");
          setSnackbarSeverity("success");
          break;
        default:
          message = t("unexpectedError");
          setSnackbarSeverity("error");
      }
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      // Refetch client history for both languages after successful assignment
      if (status === "NEW_USER" || status === "SUCCESS") {
        await queryClient.invalidateQueries({
          queryKey: ["therapistHistory", userEmail, preferredLanguage],
        });

        setTimeout(() => {
          const otherLanguage = preferredLanguage === "en" ? "de" : "en";
          queryClient.invalidateQueries({
            queryKey: ["therapistHistory", userEmail, otherLanguage],
          });
        }, 3000);
      }
    } catch (error) {
      console.error("Error sending videos:", error);
      setSnackbarMessage(t("errorSendingVideos"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    // Clear State
    setSelectedVideos(new Set());
    setFirstName("");
    setLastName("");
    setEmail("");
    handleDialogClose();
  };

  /**
   * Open client history dialog
   * Displays list of past client assignments for quick re-selection
   */
  const handleHistoryDialogOpen = () => {
    setHistoryDialogOpen(true);
  };

  /**
   * Close client history dialog
   */
  const handleHistoryDialogClose = () => {
    setHistoryDialogOpen(false);
    setHistorySearchTerm("");
  };

  /**
   * Pre-populate basket and form with previous client data
   * Used when selecting from client history
   */
  const populateBasketAndCloseDialog = (client) => {
    setSelectedVideos(new Set(client.videos.map((video) => video.id)));
    const nameParts = (client.name || "").split(" ");
    setFirstName(nameParts[0] || "");
    setLastName(nameParts.slice(1).join(" ") || "");
    setEmail(client.userEmail);
    handleHistoryDialogClose();
  };

  /**
   * Open account settings popover
   */
  const handleAccountClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAnchorEl(null);
  };

  /**
   * Open tutorial video dialog
   */
  const handleVideoClick = () => {
    setVideoDialogOpen(true);
  };

  /**
   * Close tutorial video dialog
   */
  const handleVideoClose = () => {
    setVideoDialogOpen(false);
  };

  /**
   * Handle user logout
   * 1. Set logging out flag to prevent redirect during logout
   * 2. Call logout API
   * 3. Clear TanStack Query cache
   * 4. Clear AuthStore
   * 5. Navigate to auth state (via Electron API or window redirect)
   */
  const handleLogout = useCallback(async () => {
    // Set logging out state FIRST to prevent redirects
    setIsLoggingOut(true);

    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    // Clear TanStack Query cache
    queryClient.clear();

    // Clear AuthStore
    clearAuth();

    // Small delay to ensure state updates are processed
    await new Promise((resolve) => setTimeout(resolve, 50));

    if (window.electronAPI?.navigation) {
      try {
        await window.electronAPI.navigation.navigateToAuthenticatedState(false);
      } catch (navError) {
        console.warn(
          "[PhysioVideoSelection] Electron navigation failed:",
          navError,
        );
        window.location.href = "/";
      }
    } else {
      window.location.href = "/";
    }
  }, [queryClient, clearAuth]);

  const filteredClients = useMemo(() => {
    if (!historySearchTerm.trim()) return clients;

    const term = historySearchTerm.toLowerCase();

    return clients.filter((client) => {
      const emailMatch = client.userEmail?.toLowerCase().includes(term);
      const nameMatch = client.name?.toLowerCase().includes(term);
      const videoMatch = client.videos?.some((video) =>
        video.title?.toLowerCase().includes(term),
      );

      return emailMatch || nameMatch || videoMatch;
    });
  }, [clients, historySearchTerm]);

  /**
   * Handle password update request from account popover
   * Makes API call to change-password endpoint
   * @param {string} currentPassword - Current password for verification
   * @param {string} newPassword - New password to set
   * @returns {Promise<Object>} Success/error response with message
   */
  const handlePasswordUpdate = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_FIREBASE_API_HOST}/api/auth/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true, // Send cookies
        },
      );

      if (response.data.success) {
        return { success: true, message: t("passwordUpdated") };
      } else {
        return {
          success: false,
          message: response.data.message || t("passwordUpdateFailed"),
        };
      }
    } catch (error) {
      console.error("Password update error:", error);
      return { success: false, message: t("passwordUpdateFailed") };
    }
  };

  /**
   * Toggle language between English and German
   * 1. Update Zustand auth state with new language
   * 2. Update i18n for translations
   * 3. Sync new language to backend
   * 4. Notify Electron if running as desktop app
   */
  const handleLanguageToggle = useCallback(async () => {
    const newLanguage = preferredLanguage === "en" ? "de" : "en";

    // Update Zustand
    setPreferredLanguage(newLanguage);

    // Sync i18n
    i18n.changeLanguage(newLanguage);

    // Update backend
    try {
      await settingsApi.updateLanguage(newLanguage);
    } catch (error) {
      console.error(
        "[PhysioVideoSelection] Failed to update language in backend:",
        error,
      );
    }

    // Notify Electron if present
    if (window.electronAPI?.language) {
      window.electronAPI.language.notifyChange(newLanguage);
    }
  }, [preferredLanguage, setPreferredLanguage, i18n]);

  // Request browser notification permission from user

  const handleEnablePushNotifications = async () => {
    if ("Notification" in window) {
      try {
        const permission = await Notification.requestPermission();
      } catch (error) {
        console.error("Error requesting notification permission:", error);
      }
    }
  };

  /**
   * Test notification system (stub for future implementation)
   */
  const handleTestNotifications = () => {};

  /**
   * Get unique preferences from videos in current tab
   * Used to populate preference filter dropdown
   */
  const getCurrentVideos = () => {
    return activeTab === "physio"
      ? Array.isArray(physioVideos)
        ? physioVideos
        : []
      : Array.isArray(allVideos)
        ? allVideos
        : [];
  };

  const uniquePreferenceIds = new Set();
  getCurrentVideos().forEach((video) => {
    video.preferences?.forEach((pref) => {
      uniquePreferenceIds.add(pref.id);
    });
  });

  const uniquePreferences = Array.from(uniquePreferenceIds);

  // Get all videos for finding preference names
  const allCurrentVideos = getCurrentVideos();

  // set the profile initials for the avatar (first letter of first and last name)
  const initials = user.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "DU";

  // handle home click to navigate back to dashboard
  const handleHomeClick = () => {
    navigate("/physio-dashboard");
  };

  return (
    <Container
      ref={videoPlayerRef}
      style={{ height: "calc(100vh - 200px)", paddingTop: "80px" }}
    >
      <AppBar position="fixed" sx={{ background: "#1a2332", boxShadow: "none" }}>
        <Toolbar
          sx={{
            py: 1.5,
            px: 3,
            display: "flex",
            justifyContent: isMobileView ? "center" : "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo positioned at top-left */}
          <Box style={{ display: isMobileView ? "none" : "block" }}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "auto", height: "50px" }}
              alt="DynamicDeskLogo"
            />
          </Box>

          {/* Icons positioned at top-right */}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* Helo Icon for Tutorial Video */}
            <IconButton
              onClick={handleVideoClick}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#2d3748",
                color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title="Tutorial Video"
            >
              <HelpIcon style={{ fontSize: "1.8rem" }} />
            </IconButton>

            {/* History Icon */}
            <IconButton
              onClick={handleHistoryDialogOpen}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#2d3748",
                color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title="Client History"
            >
              <HistoryIcon style={{ fontSize: "1.8rem" }} />
            </IconButton>

            {/* Basket Icon */}
            <IconButton
              onClick={handleDialogOpen}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#2d3748",
                color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title="Selected Videos"
            >
              <BasketIcon style={{ fontSize: "1.8rem" }} />
              {selectedVideos.size > 0 && (
                <span
                  style={{
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    marginLeft: "6px",
                  }}
                >
                  {selectedVideos.size}
                </span>
              )}
            </IconButton>

            {/* Home Icon for re-routing to Dashboard */}
            <IconButton
              onClick={handleHomeClick}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#2d3748",
                color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title="Home"
            >
              <HomeIcon style={{ fontSize: "1.8rem" }} />
            </IconButton>

            {/* Profile Initials Avatar */}
            <IconButton
              onClick={handleAccountClick}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#2d3748",
                color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title="Account Settings"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: "#2563EB",
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Tab Switcher */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          paddingTop: "18px",
        }}
      >
        {/* DD Videos Tab */}
        <Typography
          variant="h5"
          onClick={() => handleTabChange("dd")}
          sx={{
            color: activeTab === "dd" ? "white" : "rgba(255,255,255,0.6)",
            cursor: "pointer",
            fontWeight: activeTab === "dd" ? 600 : 400,
            textDecoration: activeTab === "dd" ? "underline" : "none",
            transition: "all 0.3s",
          }}
        >
          {t("deskDynamicsVideos") || "Desk Dynamics Übungen"}
        </Typography>

        <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>|</Typography>

        {/* Physio Videos Tab */}
        <Typography
          variant="h5"
          onClick={() => handleTabChange("physio")}
          sx={{
            color: activeTab === "physio" ? "white" : "rgba(255,255,255,0.6)",
            cursor: "pointer",
            fontWeight: activeTab === "physio" ? 600 : 400,
            textDecoration: activeTab === "physio" ? "underline" : "none",
            transition: "all 0.3s",
          }}
        >
          {t("Physio Videos") || "Übungen deiner Praxis"}
        </Typography>

        {/* Video count display */}
        <Typography
          variant="body2"
          sx={{ ml: "auto", color: "rgba(255,255,255,0.7)" }}
        >
          {filteredVideos.length}{" "}
          {filteredVideos.length === 1 ? t("video") : t("videos")}
        </Typography>
      </Box>

      {/* Search and Filter Bar */}
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label={t("search")}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ backgroundColor: "white" }}
          />
        </Grid>
        {/* Preference Filter Dropdown */}
        <Grid item xs={12} sm={4}>
          <FormControl
            fullWidth
            style={{ marginBottom: "20px" }}
            className="preferenceSelector"
          >
            {!selectedPreference && !selectOpen && (
              <InputLabel
                style={{ color: "white", backgroundColor: "#00023a" }}
              >
                {t("preference")}
              </InputLabel>
            )}
            <FormControl
              error={!selectedPreference}
              style={{ color: "white", border: "1px solid white" }}
            >
              <Select
                value={selectedPreference}
                onChange={handlePreferenceChange}
                onOpen={handleOpenSelect}
                onClose={handleCloseSelect}
                open={selectOpen}
                renderValue={(selected) => {
                  if (selected === "all") {
                    return t("selectAll");
                  } else {
                    const pref = allCurrentVideos.reduce((foundPref, video) => {
                      return (
                        foundPref ||
                        video.preferences?.find((pref) => pref.id === selected)
                      );
                    }, null);
                    return pref ? pref.name : "";
                  }
                }}
                style={{
                  color: "white",
                  border: "1px solid white",
                }}
              >
                <MenuItem value="all" style={{ color: "black" }}>
                  {t("selectAll")}
                </MenuItem>
                {uniquePreferences.map((prefId) => {
                  const pref = allCurrentVideos.reduce((foundPref, video) => {
                    return (
                      foundPref ||
                      video.preferences?.find((pref) => pref.id === prefId)
                    );
                  }, null);

                  return (
                    pref && (
                      <MenuItem key={pref.id} value={pref.id}>
                        {pref.name}
                      </MenuItem>
                    )
                  );
                })}
              </Select>
              {!selectedPreference && wasEverSelected && (
                <FormHelperText>
                  {t("pleaseChooseAtleastOneExercise")}
                </FormHelperText>
              )}
            </FormControl>
          </FormControl>
        </Grid>
      </Grid>

      {/* Video Player Modal */}
      {isPlaying && videoToPlay && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            padding: "4px",
          }}
        >
          <Box flex="1" style={{ maxWidth: "80%" }}>
            <VideoComponent
              videoId={videoToPlay.externalId}
              onClose={handleCloseVideo}
              playlist={[]}
              isPhysioSource={activeTab === "physio"}
            />
          </Box>

          <IconButton
            color="info"
            onClick={() => toggleVideoSelection(videoToPlay.id)}
            aria-label="add to basket"
            style={{ marginLeft: "16px" }}
          >
            <BasketIcon fontSize="large" />
          </IconButton>
        </Box>
      )}

      {/* Loading State */}
      {videosLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <Typography variant="h6" color="white">
            {t("loadingVideos") || "Loading videos..."}
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {videosError && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <Typography variant="h6" color="error">
            {videosError?.message ||
              t("errorLoadingVideos") ||
              "Error loading videos"}
          </Typography>
        </Box>
      )}

      {/* Video Grid Display */}
      {!videosLoading && !videosError && (
        <Grid container spacing={2} className="libraryGrid">
          {Array.isArray(filteredVideos) && filteredVideos.length > 0 ? (
            filteredVideos.map((video) => (
              <Grid
                item
                key={video.id}
                xs={12}
                sm={6}
                md={4}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Card
                  style={{
                    height: "350px",
                    cursor: "pointer",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    position: "relative",
                  }}
                >
                  <CardMedia
                    component="img"
                    alt={video.title}
                    height="200"
                    image={`${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${video.externalId}`}
                    loading="lazy"
                    sx={{
                      objectFit: "cover",
                      backgroundColor: "#000",
                    }}
                  />
                  <CardContent
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      width: "100%",
                      backgroundColor: "transparent",
                      color: "white",
                    }}
                  >
                    <Typography variant="caption" style={{ color: "white" }}>
                      {video.preferences?.map((pref) => pref.name).join(", ")}
                    </Typography>
                    <Typography variant="h6" style={{ color: "white" }}>
                      {video.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: "white",
                        maxHeight: "4em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {video.description}
                    </Typography>
                  </CardContent>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: 0,
                      transition: "opacity 0.3s",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <IconButton
                      sx={{ color: "white", mr: 2 }}
                      onClick={() => handlePlayVideo(video)}
                    >
                      <PlayArrowIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      sx={{ color: "white" }}
                      onClick={() => toggleVideoSelection(video.id)}
                    >
                      <AddCircleIcon fontSize="large" />
                    </IconButton>
                  </Box>
                  {selectedVideos.has(video.id) && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        sx={{ color: "white" }}
                        onClick={() => toggleVideoSelection(video.id)}
                      >
                        <CheckIcon
                          style={{ fontSize: "4rem", color: "white" }}
                        />
                      </IconButton>
                    </div>
                  )}
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography color="white" align="center">
                {activeTab === "physio"
                  ? t("noPhysioVideos") || "No physio videos uploaded yet"
                  : t("noVideosFound") || "No videos found"}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Send Videos to Client Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{t("selectedVideos")}</DialogTitle>
        <DialogContent>
          {/* Client first and last name inputs */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              label={t("firstName")}
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Max"
              margin="normal"
            />
            <TextField
              fullWidth
              label={t("lastName")}
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Mustermann"
              margin="normal"
            />
          </Box>
          {/* Client email input */}
          <TextField
            fullWidth
            label={t("emailAddress")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            error={email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
            helperText={
              email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                ? t("invalidEmail") || "Invalid email address"
                : ""
            }
          />
          {/* Duration number selector (1-6) */}
          <FormControl fullWidth margin="normal">
            <Select
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
              }}
            >
              {Array.from({ length: 6 }, (_, i) => i + 1).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Duration unit selector (days, weeks, months) */}
          <FormControl fullWidth margin="normal">
            <Select
              value={duration}
              onChange={(e) => {
                setDuration(e.target.value);
              }}
            >
              <MenuItem value="days">{t("Days")}</MenuItem>
              <MenuItem value="weeks">{t("Weeks")}</MenuItem>
              <MenuItem value="months">{t("Months")}</MenuItem>
            </Select>
          </FormControl>
          {/* List of selected videos with remove buttons */}
          <List>
            {Array.from(selectedVideos).map((videoId) => {
              const video = [...allVideos, ...physioVideos].find(
                (v) => v.id === videoId,
              );
              return video ? (
                <ListItem
                  key={videoId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveVideo(videoId)}
                    >
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={video.title} />
                </ListItem>
              ) : null;
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ '&:hover': { bgcolor: 'error.main', color: 'white' } }}>{t("cancel")}</Button>
          <Button onClick={handleSend} color="primary">
            {t("send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={handleHistoryDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
          },
        }}
      >
        <DialogTitle>{t("clientHistory")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder={t("searchClients")}
            value={historySearchTerm}
            onChange={(e) => setHistorySearchTerm(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <List>
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => (
                <ListItem key={client.id} alignItems="flex-start">
                  <ListItemText
                    primary={`${client.userEmail} (${client.numberOfDays} days, ${client.numberOfWeeks} weeks, ${client.numberOfMonths} months)`}
                    secondary={client.videos
                      .map((video) => video.title)
                      .join(", ")}
                  />
                  <IconButton
                    edge="end"
                    onClick={() => populateBasketAndCloseDialog(client)}
                  >
                    <EditIcon />
                  </IconButton>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={t("noClientsFound")}
                  sx={{ textAlign: "center", color: "text.secondary" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHistoryDialogClose}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* User Account Popover */}
      <UserAccountPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleAccountClose}
        onLanguageToggle={handleLanguageToggle}
        onPasswordUpdate={handlePasswordUpdate}
        onLogout={handleLogout}
        onTestNotifications={handleTestNotifications}
        currentLanguage={preferredLanguage}
        userEmail={userEmail}
        notificationPermission={getNotificationPermission()}
        onEnableNotifications={handleEnablePushNotifications}
        isElectron={isElectron}
      />

      {/* Video Tutorial Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleVideoClose}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={handleVideoClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <ReactPlayer
            url="https://vimeo.com/1059501770"
            controls
            width="100%"
            height="400px"
            playing={true}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PhysioVideoSelection;
